/**
 * A single ledger row in the rewards transaction history.
 *
 * Credits read green with an up arrow; debits (redemptions / expiries) read
 * amber with a down arrow. All colors resolve to design tokens / palette CSS
 * vars so the row stays correct in dark mode.
 */
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { formatDate } from '@finmin/utils';
import type { RewardTransactionResponse } from '@/api/rewards';
import { daysUntilExpiry, formatPoints, isCredit, sourceLabel } from '../utils';

const PALETTE_FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';

export function TransactionItem({ tx }: { tx: RewardTransactionResponse }) {
  const credit = isCredit(tx.entry_type);
  const expiresInDays = credit ? daysUntilExpiry(tx.expires_at) : null;

  return (
    <li className="flex items-center gap-3 py-3">
      {/* Direction icon */}
      <span
        className={[
          'h-9 w-9 rounded-full flex items-center justify-center shrink-0',
          credit ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning',
        ].join(' ')}
        aria-hidden
      >
        {credit ? (
          <ArrowDownRight className="h-4 w-4" />
        ) : (
          <ArrowUpRight className="h-4 w-4" />
        )}
      </span>

      {/* Description + meta */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate">
          {tx.description?.trim() || sourceLabel(tx.source)}
        </p>
        <p className="text-xs text-muted-foreground">
          {sourceLabel(tx.source)} · {formatDate(tx.created_at)}
          {expiresInDays != null && (
            <span className={expiresInDays <= 30 ? 'text-warning' : undefined}>
              {' '}· expires in {expiresInDays}d
            </span>
          )}
        </p>
      </div>

      {/* Signed points */}
      <span
        className="text-sm font-semibold tabular-nums shrink-0"
        style={credit ? { color: PALETTE_FOREST } : undefined}
      >
        <span className={credit ? undefined : 'text-warning'}>
          {credit ? '+' : '−'}
          {formatPoints(tx.points)} pts
        </span>
      </span>
    </li>
  );
}
